import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import client from '../../api/client';
import { formatDate as formatDateUtil } from '../../utils/dateParser';
import { formatDateForInput, parseDateLocal } from '../../utils/scheduleHelpers';
import { applyCascadeToTasks } from '../../utils/cascadeDates';
import { useTheme } from '../../context/ThemeContext';
import {
  GANTT_DEPARTMENT_OPTIONS,
  GANTT_TYPE_COLORS,
  SCHEDULE_DEPARTMENT_LABELS,
  getScheduleStageLabel,
  getScheduleWorkLabel,
} from '../../utils/scheduleDisplayUtils';
import {
  clearDepartments,
  departmentFilterLabel,
  isDepartmentChecked,
  passesDepartmentFilter,
  selectAllDepartments,
  toggleDepartmentSelection,
} from '../../utils/ganttDepartmentsFilter';
import {
  animateSyncedScroll,
  clampScroll,
  expandRangeToIncludeToday,
  todayXOffset,
} from '../../utils/ganttTimeline';

const DepartmentsFilter = ({ options, selected, onChange, isDark }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  useEffect(() => {
    const h = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, []);
  const allKeys = options.map(o => o.key);
  const toggleDept = (key) => {
    onChange(toggleDepartmentSelection(selected, allKeys, key));
  };
  const isChecked = (key) => isDepartmentChecked(selected, key);
  const selectAll = () => onChange(selectAllDepartments());
  const resetFilter = () => onChange(clearDepartments());
  const plural = (n) => {
    if (n === 1) return 'отдел';
    if (n >= 2 && n <= 4) return 'отдела';
    return 'отделов';
  };
  const label = departmentFilterLabel(selected, plural);
  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="gantt-departments-trigger"
        style={{
          padding: '8px 14px',
          borderRadius: '8px',
          border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
          background: isDark ? '#1e293b' : '#fff',
          color: isDark ? '#f8fafc' : '#1e293b',
          fontSize: '13px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          minWidth: '160px'
        }}
      >
        <span>Отделы:</span>
        <span style={{ fontWeight: '600' }} data-testid="gantt-departments-label">{label}</span>
        <span style={{ marginLeft: 'auto' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '4px',
            padding: '12px',
            borderRadius: '8px',
            border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
            background: isDark ? '#1e293b' : '#fff',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 2000,
            minWidth: '220px'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', gap: '8px' }}>
            <button
              type="button"
              onClick={selectAll}
              data-testid="gantt-departments-select-all"
              style={{
                padding: '4px 10px',
                fontSize: '11px',
                border: 'none',
                borderRadius: '4px',
                background: isDark ? '#334155' : '#f1f5f9',
                color: isDark ? '#94a3b8' : '#64748b',
                cursor: 'pointer'
              }}
            >
              Все
            </button>
            <button
              type="button"
              onClick={resetFilter}
              data-testid="gantt-departments-reset"
              style={{
                padding: '4px 10px',
                fontSize: '11px',
                border: 'none',
                borderRadius: '4px',
                background: isDark ? '#334155' : '#f1f5f9',
                color: isDark ? '#94a3b8' : '#64748b',
                cursor: 'pointer'
              }}
            >
              Сбросить
            </button>
          </div>
          {options.map(({ key, label: lb }) => {
            const checked = isChecked(key);
            return (
              <label
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '6px 0',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: isDark ? '#f8fafc' : '#1e293b'
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleDept(key)}
                  style={{ width: '16px', height: '16px' }}
                />
                {lb}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DEFAULT_UNDATED_DURATION_DAYS = 6;

/** Плановые даты для отображения в Ганте; без дат — условный интервал от сегодня */
const resolvePlannedDates = (schedule) => {
  let plannedStart = schedule.planned_start_date
    ? parseDateLocal(schedule.planned_start_date)
    : null;
  let plannedEnd = schedule.planned_end_date
    ? parseDateLocal(schedule.planned_end_date)
    : null;
  const hasStart = plannedStart && !isNaN(plannedStart.getTime());
  const hasEnd = plannedEnd && !isNaN(plannedEnd.getTime());
  const isUndated = !hasStart && !hasEnd;

  if (isUndated) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    plannedStart = new Date(today);
    plannedEnd = new Date(today);
    plannedEnd.setDate(plannedEnd.getDate() + DEFAULT_UNDATED_DURATION_DAYS);
    return { plannedStart, plannedEnd, isUndated: true };
  }
  if (hasStart && !hasEnd) {
    plannedEnd = new Date(plannedStart);
    plannedEnd.setDate(plannedEnd.getDate() + DEFAULT_UNDATED_DURATION_DAYS);
    return { plannedStart, plannedEnd, isUndated: false, partialDates: true };
  }
  if (!hasStart && hasEnd) {
    plannedStart = new Date(plannedEnd);
    plannedStart.setDate(plannedStart.getDate() - DEFAULT_UNDATED_DURATION_DAYS);
    return { plannedStart, plannedEnd, isUndated: false, partialDates: true };
  }
  return { plannedStart, plannedEnd, isUndated: false, partialDates: false };
};

const ModernGanttChart = ({
  schedules,
  cities,
  selectedView = null,
  chartTitle = null,
  allowedScheduleTypes = null,
  onScheduleUpdate,
  defaultDragDisabled = false,
  dependencyCityId = null
}) => {
  const { isDark } = useTheme();
  const [dragModeEnabled, setDragModeEnabled] = useState(!defaultDragDisabled);
  const [tasks, setTasks] = useState([]);
  const DEPT_OPTIONS = GANTT_DEPARTMENT_OPTIONS;
  const [selectedDepartments, setSelectedDepartments] = useState(null); // null = все отделы; Set() = ничего
  const ZOOM_MIN = 1;
  const ZOOM_MAX = 40;
  const SCALE_PRESETS = [
    { key: 'year', label: 'Год', value: 1, icon: '📅' },
    { key: 'quarter', label: 'Квартал', value: 2.5, icon: '📆' },
    { key: 'month', label: 'Месяц', value: 4, icon: '🗓️' },
    { key: 'week', label: 'Неделя', value: 12, icon: '📊' },
    { key: 'day', label: 'День', value: 30, icon: '🔍' }
  ];
  const [zoomLevel, setZoomLevel] = useState(12);
  const [showCriticalPath, setShowCriticalPath] = useState(false);
  const [cpmData, setCpmData] = useState({ nodes: [], edges: [], criticalStages: [], criticalTaskIds: [] });
  const [dragging, setDragging] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [hoveredTask, setHoveredTask] = useState(null);
  const [hoverAnchorRect, setHoverAnchorRect] = useState(null);
  const [sortBy, setSortBy] = useState('stage'); // date, stage, type
  const [syncCount, setSyncCount] = useState(0); // Счётчик активных операций синхронизации
  const isSyncing = syncCount > 0; // Индикатор синхронизации (вычисляемое значение)
  const [yearFilter, setYearFilter] = useState('all'); // Фильтр по году
  const [startYear, setStartYear] = useState(null); // Начальный год диапазона
  const [endYear, setEndYear] = useState(null); // Конечный год диапазона
  const [stageFilter, setStageFilter] = useState(''); // Фильтр по этапу строительства
  const [stages, setStages] = useState([]); // Список этапов строительства
  
  const sidebarRef = useRef(null);
  const timelineBodyRef = useRef(null);
  const timelineHeaderRef = useRef(null);
  const rafRef = useRef(null); // Для requestAnimationFrame
  const pendingUpdateRef = useRef(null); // Для хранения отложенного обновления
  const prevDayWidthRef = useRef(null);
  const cascadeEdgesRef = useRef([]);
  const tasksSnapshotRef = useRef(null);
  const barClickMovedRef = useRef(false);
  const scrollAnimRef = useRef(null);
  const [todayPulse, setTodayPulse] = useState(false);
  const stopScrollAnim = useCallback(() => {
    if (scrollAnimRef.current) {
      scrollAnimRef.current();
      scrollAnimRef.current = null;
    }
  }, []);

  // Константы
  const ROW_HEIGHT = 56;
  const HEADER_HEIGHT = 70;
  const TASK_HEIGHT = 40;
  const SIDEBAR_WIDTH = 340;
  const DAY_WIDTH = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zoomLevel));

  const typeColors = GANTT_TYPE_COLORS;
  const typeNames = SCHEDULE_DEPARTMENT_LABELS;

  // Форматирование даты
  // Используем утилиту форматирования дат (DD/MM/YYYY)
  const formatDate = (date) => {
    if (!date) return '';
    return formatDateUtil(date);
  };

  const formatDateShort = (date) => {
    if (!date) return '';
    const formatted = formatDateUtil(date);
    // Возвращаем только DD/MM (без года)
    return formatted.substring(0, 5);
  };

  const clearTaskHover = useCallback(() => {
    setHoveredTask(null);
    setHoverAnchorRect(null);
  }, []);

  const setTaskHover = useCallback((taskId, el) => {
    setHoveredTask(taskId);
    if (!el) {
      setHoverAnchorRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setHoverAnchorRect({
      top: r.top,
      left: r.left,
      bottom: r.bottom,
      right: r.right,
      width: r.width,
      height: r.height,
    });
  }, []);

  // Синхронизация скролла
  const handleSidebarScroll = (e) => {
    clearTaskHover();
    if (timelineBodyRef.current) {
      timelineBodyRef.current.scrollTop = e.target.scrollTop;
    }
  };

  const handleTimelineScroll = (e) => {
    clearTaskHover();
    // Синхронизация вертикального скролла с sidebar
    if (sidebarRef.current) {
      sidebarRef.current.scrollTop = e.target.scrollTop;
    }
    // Синхронизация горизонтального скролла с заголовком таймлайна
    if (timelineHeaderRef.current) {
      timelineHeaderRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  const handleTimelineHeaderScroll = (e) => {
    clearTaskHover();
    // Синхронизация горизонтального скролла с областью задач
    if (timelineBodyRef.current) {
      timelineBodyRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  // Масштабирование колесом мыши при Ctrl (на таймлайне)
  useEffect(() => {
    const el = timelineBodyRef.current;
    if (!el) return;
    const handler = (e) => {
      stopScrollAnim();
      if (!e.ctrlKey) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -2 : 2;
      setZoomLevel(prev => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, prev + delta)));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [stopScrollAnim]);

  useEffect(() => () => stopScrollAnim(), [stopScrollAnim]);

  // Сохранение позиции скролла при смене масштаба
  useEffect(() => {
    const prev = prevDayWidthRef.current;
    prevDayWidthRef.current = DAY_WIDTH;
    if (prev == null) return;
    const ratio = DAY_WIDTH / prev;
    // Откладываем до следующего кадра — даём React обновить DOM с новыми размерами
    const id = requestAnimationFrame(() => {
      const body = timelineBodyRef.current;
      const header = timelineHeaderRef.current;
      if (body && ratio !== 1) {
        const newScrollLeft = Math.min(
          Math.max(0, body.scrollWidth - body.clientWidth),
          body.scrollLeft * ratio
        );
        body.scrollLeft = newScrollLeft;
      }
      if (header && ratio !== 1) {
        const newScrollLeft = Math.min(
          Math.max(0, header.scrollWidth - header.clientWidth),
          header.scrollLeft * ratio
        );
        header.scrollLeft = newScrollLeft;
      }
    });
    return () => cancelAnimationFrame(id);
  }, [DAY_WIDTH]);

  // Загрузка этапов строительства
  useEffect(() => {
    const fetchStages = async () => {
      try {
        const response = await client.get('/construction-stages?active_only=true');
        // Сортируем по order_index для правильного порядка
        const sortedStages = response.data.sort((a, b) => a.order_index - b.order_index);
        setStages(sortedStages);
      } catch (error) {
        console.error('Error fetching stages:', error);
      }
    };
    fetchStages();
  }, []);

  // Границы диапазона годов (мин/макс из данных с запасом)
  const yearRangeLimits = useMemo(() => {
    if (tasks.length === 0) return { min: 2018, max: 2035 };
    const years = new Set();
    tasks.forEach(task => {
      if (task.plannedStart) years.add(task.plannedStart.getFullYear());
      if (task.plannedEnd) years.add(task.plannedEnd.getFullYear());
      if (task.actualStart) years.add(task.actualStart.getFullYear());
      if (task.actualEnd) years.add(task.actualEnd.getFullYear());
    });
    const yearsArray = Array.from(years);
    if (yearsArray.length === 0) return { min: 2018, max: 2035 };
    
    const minYear = Math.min(...yearsArray);
    const maxYear = Math.max(...yearsArray);
    return {
      min: Math.max(2018, minYear - 2), // С запасом -2 года
      max: Math.min(2035, maxYear + 2)  // С запасом +2 года
    };
  }, [tasks]);

  // Доступные годы из задач (для выпадающего списка)
  const availableYears = useMemo(() => {
    if (tasks.length === 0) return [];
    const years = new Set();
    tasks.forEach(task => {
      if (task.plannedStart) years.add(task.plannedStart.getFullYear());
      if (task.plannedEnd) years.add(task.plannedEnd.getFullYear());
      if (task.actualStart) years.add(task.actualStart.getFullYear());
      if (task.actualEnd) years.add(task.actualEnd.getFullYear());
    });
    return Array.from(years).sort((a, b) => a - b);
  }, [tasks]);

  // Находим самую раннюю и самую позднюю даты из всех задач
  const earliestDate = useMemo(() => {
    if (tasks.length === 0) return null;
    const dates = [];
    tasks.forEach(task => {
      if (task.plannedStart) dates.push(task.plannedStart);
      if (task.actualStart) dates.push(task.actualStart);
    });
    return dates.length > 0 ? new Date(Math.min(...dates.map(d => d.getTime()))) : null;
  }, [tasks]);

  const latestDate = useMemo(() => {
    if (tasks.length === 0) return null;
    const dates = [];
    tasks.forEach(task => {
      if (task.plannedEnd) dates.push(task.plannedEnd);
      if (task.actualEnd) dates.push(task.actualEnd);
    });
    return dates.length > 0 ? new Date(Math.max(...dates.map(d => d.getTime()))) : null;
  }, [tasks]);

  // Инициализация диапазона годов на основе данных
  useEffect(() => {
    if (availableYears.length > 0) {
      const minYearVal = availableYears[0];
      const maxYearVal = availableYears[availableYears.length - 1];
      
      // Устанавливаем только если еще не установлены
      if (startYear === null) {
        setStartYear(minYearVal);
      }
      if (endYear === null) {
        setEndYear(maxYearVal);
      }
    }
  }, [availableYears, startYear, endYear]);

  // Используем вычисленные значения или дефолтные
  const activeStartYear = startYear ?? 2022;
  const activeEndYear = endYear ?? 2028;

  // Фильтруем задачи по году или диапазону и по этапу строительства
  const filteredTasks = useMemo(() => {
    let filtered = tasks.filter(task => {
      const taskStartYear = task.plannedStart?.getFullYear();
      const taskEndYear = task.plannedEnd?.getFullYear();
      const actualStartYear = task.actualStart?.getFullYear();
      const actualEndYear = task.actualEnd?.getFullYear();
      
      // Если выбран конкретный год
      if (yearFilter !== 'all') {
        const year = parseInt(yearFilter);
        return taskStartYear === year || taskEndYear === year || 
               actualStartYear === year || actualEndYear === year ||
               (taskStartYear && taskEndYear && taskStartYear <= year && taskEndYear >= year);
      }
      
      // Если выбран диапазон (yearFilter === 'all')
      const hasOverlap = (taskStart, taskEnd) => {
        if (!taskStart || !taskEnd) return false;
        return taskEnd >= activeStartYear && taskStart <= activeEndYear;
      };
      
      return hasOverlap(taskStartYear, taskEndYear) || 
             hasOverlap(actualStartYear, actualEndYear) ||
             (taskStartYear >= activeStartYear && taskStartYear <= activeEndYear) ||
             (taskEndYear >= activeStartYear && taskEndYear <= activeEndYear);
    });
    
    // Фильтр по этапу строительства
    if (stageFilter) {
      filtered = filtered.filter(task => task.constructionStage === stageFilter);
    }
    
    return filtered;
  }, [tasks, yearFilter, activeStartYear, activeEndYear, stageFilter]);

  // Связи с задачами других отделов / вне текущего списка (не рисуются на этом Gantt)
  const hiddenLinkCount = useMemo(() => {
    const ids = new Set(tasks.map((t) => t.id));
    return (cpmData.edges || []).filter(
      (e) => !ids.has(e.source) || !ids.has(e.target)
    ).length;
  }, [cpmData.edges, tasks]);

  // Временной диапазон
  const timeRange = useMemo(() => {
    let minDate, maxDate;
    
    // Если фильтр по конкретному году активен
    if (yearFilter !== 'all') {
      const year = parseInt(yearFilter);
      // СТРОГО ограничиваем год - не выходим за его пределы
      minDate = new Date(year, 0, 1);
      maxDate = new Date(year, 11, 31);
    } else {
      // Если показываем диапазон годов через слайдеры
      // Всегда используем месяц самой ранней задачи для начала диапазона
      if (earliestDate) {
        minDate = new Date(earliestDate.getFullYear(), earliestDate.getMonth(), 1);
      } else {
        minDate = new Date(activeStartYear, 0, 1);
      }
      
      // Всегда используем месяц самой поздней задачи для конца диапазона
      if (latestDate) {
        maxDate = new Date(latestDate.getFullYear(), latestDate.getMonth() + 1, 0);
      } else {
        maxDate = new Date(activeEndYear, 11, 31);
      }
    }
    
    // Добавляем небольшой запас для лучшей визуализации
    const bufferedMin = new Date(minDate.getFullYear(), minDate.getMonth() - 1, 1);
    const bufferedMax = new Date(maxDate.getFullYear(), maxDate.getMonth() + 2, 0);
    const { min: finalMin, max: finalMax } = expandRangeToIncludeToday(bufferedMin, bufferedMax);

    const days = Math.round((finalMax - finalMin) / (1000 * 60 * 60 * 24)) + 1;
    return { start: finalMin, end: finalMax, days };
  }, [yearFilter, activeStartYear, activeEndYear, earliestDate, latestDate]);

  // Заголовки месяцев
  const monthHeaders = useMemo(() => {
    const headers = [];
    if (timeRange.days === 0) return headers;
    
    const current = new Date(timeRange.start);
    while (current <= timeRange.end) {
      const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0);
      const daysInMonth = Math.min(
        monthEnd.getDate() - current.getDate() + 1,
        Math.ceil((timeRange.end - current) / (1000 * 60 * 60 * 24)) + 1
      );
      
      headers.push({
        label: current.toLocaleDateString('ru-RU', { month: 'short', year: 'numeric' }),
        width: daysInMonth * DAY_WIDTH,
        startDay: current.getDate()
      });
      
      current.setMonth(current.getMonth() + 1);
      current.setDate(1);
    }
    return headers;
  }, [timeRange, DAY_WIDTH]);

  // Локальный CPM алгоритм (fallback) - должен быть определён ДО fetchDependencyGraph
  const calculateCPMLocal = useCallback((taskList) => {
    if (taskList.length === 0) return { nodes: [], edges: [], criticalStages: [], criticalTaskIds: [] };

    const byStage = {};
    taskList.forEach(task => {
      const stage = task.constructionStage || 'Без этапа';
      if (!byStage[stage]) byStage[stage] = [];
      byStage[stage].push(task);
    });

    const stageOrder = Object.keys(byStage).sort((a, b) => {
      const minA = Math.min(...byStage[a].map(t => t.plannedStart.getTime()));
      const minB = Math.min(...byStage[b].map(t => t.plannedStart.getTime()));
      return minA - minB;
    });

    const nodes = stageOrder.map((stage, idx) => {
      const stageTasks = byStage[stage];
      const start = new Date(Math.min(...stageTasks.map(t => t.plannedStart.getTime())));
      const end = new Date(Math.max(...stageTasks.map(t => t.plannedEnd.getTime())));
      const duration = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      
      return {
        id: idx, stage, duration,
        ES: 0, EF: 0, LS: 0, LF: 0, float: 0, isCritical: false,
        predecessors: idx > 0 ? [idx - 1] : [],
        successors: idx < stageOrder.length - 1 ? [idx + 1] : [],
        taskIds: stageTasks.map(t => t.id)
      };
    });

    if (nodes.length === 0) return { nodes: [], edges: [], criticalStages: [], criticalTaskIds: [] };

    // Forward Pass
    nodes.forEach(node => {
      node.ES = node.predecessors.length === 0 ? 0 : Math.max(...node.predecessors.map(i => nodes[i].EF));
      node.EF = node.ES + node.duration;
    });

    // Backward Pass
    const projectDuration = Math.max(...nodes.map(n => n.EF));
    for (let i = nodes.length - 1; i >= 0; i--) {
      const node = nodes[i];
      node.LF = node.successors.length === 0 ? projectDuration : Math.min(...node.successors.map(j => nodes[j].LS));
      node.LS = node.LF - node.duration;
    }

    nodes.forEach(node => {
      node.float = node.LS - node.ES;
      node.isCritical = node.float === 0;
    });

    const criticalStages = nodes.filter(n => n.isCritical).map(n => n.stage);
    const criticalTaskIds = nodes.filter(n => n.isCritical).flatMap(n => n.taskIds || []);

    return { nodes, edges: [], criticalStages, criticalTaskIds };
  }, []);

  // Загрузка графа зависимостей (рёбра + КП)
  const fetchDependencyGraph = useCallback(async () => {
    try {
      const params = {};
      if (dependencyCityId) params.city_id = dependencyCityId;
      const response = await client.get('/dependencies/dependency-graph', { params });
      const { nodes, edges, critical_path } = response.data;

      const criticalStages = new Set();
      const path = Array.isArray(critical_path) ? critical_path : [];
      (nodes || []).forEach((node) => {
        if (path.includes(node.id)) {
          criticalStages.add(node.stage);
        }
      });

      return {
        nodes: nodes || [],
        edges: edges || [],
        criticalStages: Array.from(criticalStages),
        criticalTaskIds: path,
      };
    } catch (error) {
      console.error('Ошибка загрузки графа зависимостей:', error);
      return calculateCPMLocal(tasksRef.current);
    }
  }, [calculateCPMLocal, dependencyCityId]);

  // Рёбра всегда для каскада/стрелок; КП только подсветка
  useEffect(() => {
    if (!dependencyCityId) {
      cascadeEdgesRef.current = [];
      setCpmData({ nodes: [], edges: [], criticalStages: [], criticalTaskIds: [] });
      return undefined;
    }
    let cancelled = false;
    fetchDependencyGraph().then((data) => {
      if (cancelled) return;
      cascadeEdgesRef.current = data.edges || [];
      if (showCriticalPath) {
        setCpmData(data);
      } else {
        setCpmData({
          nodes: data.nodes || [],
          edges: data.edges || [],
          criticalStages: [],
          criticalTaskIds: [],
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [dependencyCityId, showCriticalPath, fetchDependencyGraph, Array.isArray(schedules) ? schedules.length : 0]);

  // Функция сортировки
  const sortTasks = useCallback((taskList, sortType, stagesList = []) => {
    const sorted = [...taskList];
    switch (sortType) {
      case 'date':
        return sorted.sort((a, b) => a.plannedStart - b.plannedStart);
      case 'date-desc':
        return sorted.sort((a, b) => b.plannedStart - a.plannedStart);
      case 'stage':
        return sorted.sort((a, b) => {
          const stageA = stagesList.find(s => s.name === a.constructionStage);
          const stageB = stagesList.find(s => s.name === b.constructionStage);
          const orderA = stageA ? stageA.order_index : 999;
          const orderB = stageB ? stageB.order_index : 999;
          return orderA - orderB;
        });
      case 'type':
        return sorted.sort((a, b) => a.type.localeCompare(b.type));
      case 'city':
        return sorted.sort((a, b) => (a.cityName || '').localeCompare(b.cityName || ''));
      case 'duration':
        return sorted.sort((a, b) => {
          const durA = (a.plannedEnd - a.plannedStart) / (1000 * 60 * 60 * 24) + 1;
          const durB = (b.plannedEnd - b.plannedStart) / (1000 * 60 * 60 * 24) + 1;
          return durA - durB;
        });
      default:
        return sorted;
    }
  }, []);

  // Обработка данных
  useEffect(() => {
    if (!schedules || schedules.length === 0) {
      if (!dragging && !resizing && !isSyncing) {
        setTasks([]);
      }
      return;
    }

    // БАГ-ФИХ: Не обновляем tasks если идёт перетаскивание или сохранение
    // Это предотвращает потерю изменений при обновлении schedules извне
    if (dragging || resizing || isSyncing) {
      return;
    }

    const processed = schedules
      .filter(s => passesDepartmentFilter(selectedDepartments, s.schedule_type))
      .filter(s => !allowedScheduleTypes || allowedScheduleTypes.includes(s.schedule_type))
      .map(schedule => {
        const city = cities.find(c => c.id === schedule.city_id);
        const { plannedStart, plannedEnd, isUndated, partialDates } = resolvePlannedDates(schedule);
        if (!plannedStart || !plannedEnd || isNaN(plannedStart.getTime()) || isNaN(plannedEnd.getTime())) {
          return null;
        }
        return {
          id: schedule.id,
          cityName: city?.name || 'Неизвестный объект',
          constructionStage: getScheduleStageLabel(schedule),
          workName: getScheduleWorkLabel(schedule),
          type: schedule.schedule_type,
          plannedStart,
          plannedEnd,
          actualStart: schedule.actual_start_date ? parseDateLocal(schedule.actual_start_date) : null,
          actualEnd: schedule.actual_end_date ? parseDateLocal(schedule.actual_end_date) : null,
          color: typeColors[schedule.schedule_type] || typeColors.construction,
          isUndated,
          partialDates,
          scheduleRef: schedule,
        };
      }).filter(Boolean);

    const sortedProcessed = sortTasks(processed, sortBy, stages);

    // Оптимизация: не вызывать setTasks если данные по сути не изменились
    // (предотвращает перерисовку всей диаграммы после фонового refetch)
    const current = tasksRef.current;
    if (current.length === sortedProcessed.length) {
      const same = sortedProcessed.every((p, i) => {
        const t = current[i];
        if (!t || t.id !== p.id) return false;
        const ts = (d) => d ? d.getTime() : null;
        return ts(t.plannedStart) === ts(p.plannedStart) &&
          ts(t.plannedEnd) === ts(p.plannedEnd) &&
          ts(t.actualStart) === ts(p.actualStart) &&
          ts(t.actualEnd) === ts(p.actualEnd);
      });
      if (same) return;
    }

    setTasks(sortedProcessed);
  }, [schedules, cities, selectedDepartments, allowedScheduleTypes, sortBy, sortTasks, dragging, resizing, isSyncing, stages]);

  // (edges всегда загружены выше; КП только меняет подсветку через тот же effect)

  // Позиция плановых дат (для фонового отображения)
  const getPlannedPosition = useCallback((task) => {
    const startOffset = Math.ceil((task.plannedStart - timeRange.start) / (1000 * 60 * 60 * 24));
    const duration = Math.ceil((task.plannedEnd - task.plannedStart) / (1000 * 60 * 60 * 24)) + 1;
    return {
      left: startOffset * DAY_WIDTH,
      width: Math.max(duration * DAY_WIDTH, 30)
    };
  }, [timeRange.start, DAY_WIDTH]);

  // Позиция фактических дат (основной бар, который можно двигать)
  const getTaskPosition = useCallback((task) => {
    // Используем фактические даты если есть, иначе плановые
    const start = task.actualStart || task.plannedStart;
    const end = task.actualEnd || task.plannedEnd;
    
    const startOffset = Math.ceil((start - timeRange.start) / (1000 * 60 * 60 * 24));
    const duration = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    return {
      left: startOffset * DAY_WIDTH,
      width: Math.max(duration * DAY_WIDTH, 30)
    };
  }, [timeRange.start, DAY_WIDTH]);

  const scrollTimelineTo = useCallback((centerX, nextTop, { pulseToday = false } = {}) => {
    const body = timelineBodyRef.current;
    const header = timelineHeaderRef.current;
    const sidebar = sidebarRef.current;
    if (!body) return;

    stopScrollAnim();

    const fromLeft = body.scrollLeft;
    const fromTop = body.scrollTop;
    const targetLeft = centerX - body.clientWidth / 2;
    const { left, top } = clampScroll(
      body,
      targetLeft,
      nextTop == null ? fromTop : nextTop
    );

    const apply = ({ left: nextLeft, top: nextY }) => {
      body.scrollLeft = nextLeft;
      if (header) header.scrollLeft = nextLeft;
      if (nextTop != null) {
        body.scrollTop = nextY;
        if (sidebar) sidebar.scrollTop = nextY;
      }
    };

    if (Math.abs(left - fromLeft) < 1 && (nextTop == null || Math.abs(top - fromTop) < 1)) {
      apply({ left, top });
      if (pulseToday) {
        setTodayPulse(true);
        window.setTimeout(() => setTodayPulse(false), 700);
      }
      return;
    }

    scrollAnimRef.current = animateSyncedScroll({
      fromLeft,
      fromTop,
      toLeft: left,
      toTop: top,
      apply: (frame) => {
        apply(frame);
        if (frame.progress >= 1) {
          scrollAnimRef.current = null;
          if (pulseToday) {
            setTodayPulse(true);
            window.setTimeout(() => setTodayPulse(false), 700);
          }
        }
      },
    });
  }, [stopScrollAnim]);

  /** Скролл таймлайна к фактическим датам задачи, иначе к плановым. */
  const scrollToTask = useCallback((taskId) => {
    const body = timelineBodyRef.current;
    if (!body) return;

    const index = filteredTasks.findIndex((t) => t.id === taskId);
    if (index < 0) return;
    const task = filteredTasks[index];

    const hasActualDates = Boolean(task.actualStart && task.actualEnd);
    const pos = hasActualDates ? getTaskPosition(task) : getPlannedPosition(task);
    const barCenter = pos.left + pos.width / 2;

    const rowTop = index * ROW_HEIGHT;
    let nextTop = body.scrollTop;
    if (rowTop < body.scrollTop) {
      nextTop = rowTop;
    } else if (rowTop + ROW_HEIGHT > body.scrollTop + body.clientHeight) {
      nextTop = rowTop + ROW_HEIGHT - body.clientHeight;
    }

    scrollTimelineTo(barCenter, nextTop);
  }, [filteredTasks, getPlannedPosition, getTaskPosition, ROW_HEIGHT, scrollTimelineTo]);

  const todayOffset = useMemo(
    () => todayXOffset(timeRange.start, timeRange.end, DAY_WIDTH),
    [timeRange.start, timeRange.end, DAY_WIDTH]
  );

  const scrollToToday = useCallback(() => {
    if (todayOffset == null) return;
    scrollTimelineTo(todayOffset, null, { pulseToday: true });
  }, [todayOffset, scrollTimelineTo]);

  // Drag handlers - используем ref для хранения состояния перетаскивания
  const dragStateRef = useRef(null);
  const resizeStateRef = useRef(null);
  const originalTaskRef = useRef(null); // Сохраняем оригинальную задачу
  const tasksRef = useRef(tasks); // Ref для доступа к tasks без пересоздания callback
  
  // Обновляем ref при изменении tasks
  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);
  
  const handleMouseDown = (e, task, action) => {
    stopScrollAnim();
    barClickMovedRef.current = false;
    const canDrag = onScheduleUpdate && (!defaultDragDisabled || dragModeEnabled) && !task.isUndated;
    if (!canDrag) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    
    // БАГ-ФИХ: Сохраняем полную копию задачи для отката
    originalTaskRef.current = { ...task };
    tasksSnapshotRef.current = tasksRef.current.map((t) => ({
      ...t,
      plannedStart: t.plannedStart ? new Date(t.plannedStart.getTime()) : null,
      plannedEnd: t.plannedEnd ? new Date(t.plannedEnd.getTime()) : null,
      actualStart: t.actualStart ? new Date(t.actualStart.getTime()) : null,
      actualEnd: t.actualEnd ? new Date(t.actualEnd.getTime()) : null
    }));
    
    // Используем фактические даты если есть, иначе плановые
    const currentStart = task.actualStart || task.plannedStart;
    const currentEnd = task.actualEnd || task.plannedEnd;
    
    // Сохраняем timestamp оригинальных дат чтобы избежать мутации
    const originalStartTime = currentStart.getTime();
    const originalEndTime = currentEnd.getTime();
    
    // БАГ-ФИХ: Сохраняем был ли у задачи actualStart/actualEnd до перемещения
    const hadActualDates = !!(task.actualStart && task.actualEnd);
    
    if (action === 'move') {
      const state = { 
        taskId: task.id, 
        startX, 
        originalStartTime,
        originalEndTime,
        hadActualDates,
        lastDaysDelta: 0
      };
      dragStateRef.current = state;
      setDragging(state);
    } else {
      const state = { 
        taskId: task.id, 
        startX, 
        action, 
        originalStartTime,
        originalEndTime,
        hadActualDates,
        lastDaysDelta: 0
      };
      resizeStateRef.current = state;
      setResizing(state);
    }
  };

  // Минимальное перемещение в пикселях для создания факт. дат (если их не было)
  const MIN_DRAG_PX = 15;

  const handleMouseMove = useCallback((e) => {
    const dragState = dragStateRef.current;
    const resizeState = resizeStateRef.current;
    
    if (!dragState && !resizeState) return;
    
    const currentX = e.clientX;
    const state = dragState || resizeState;
    
    // Рассчитываем смещение в днях от начала перетаскивания
    const totalDeltaX = currentX - state.startX;
    
    // БАГ-ФИХ: Если у задачи не было фактических дат — не создавать их при микродвижении.
    // Требуем минимальное перемещение, иначе случайный клик может "записать" план как факт.
    if (Math.abs(totalDeltaX) < MIN_DRAG_PX) return;
    
    const daysDelta = Math.round(totalDeltaX / DAY_WIDTH);
    
    // Обновляем только если смещение в днях изменилось
    if (daysDelta === state.lastDaysDelta) return;
    state.lastDaysDelta = daysDelta;
    barClickMovedRef.current = true;

    // Сохраняем данные для обновления
    pendingUpdateRef.current = { dragState, resizeState, daysDelta };
    
    // Используем requestAnimationFrame для плавного обновления
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const pending = pendingUpdateRef.current;
        if (!pending) return;
        
        const { dragState: ds, resizeState: rs, daysDelta: delta } = pending;
        
        if (ds) {
          const newStart = new Date(ds.originalStartTime + delta * 24 * 60 * 60 * 1000);
          const newEnd = new Date(ds.originalEndTime + delta * 24 * 60 * 60 * 1000);
          
          // БАГ-ФИХ: Обновляем ФАКТИЧЕСКИЕ даты (создаём их если не было)
          setTasks((prev) => {
            let next = prev.map((t) =>
              t.id === ds.taskId
                ? {
                    ...t,
                    actualStart: newStart,
                    actualEnd: newEnd
                  }
                : t
            );
            if (dependencyCityId && cascadeEdgesRef.current.length) {
              next = applyCascadeToTasks(next, cascadeEdgesRef.current, ds.taskId);
            }
            return next;
          });
        }
        
        if (rs) {
          if (rs.action === 'resize-end') {
            const newEnd = new Date(rs.originalEndTime + delta * 24 * 60 * 60 * 1000);
            const originalStart = new Date(rs.originalStartTime);
            
            if (newEnd > originalStart) {
              setTasks((prev) => {
                let next = prev.map((t) => {
                  if (t.id === rs.taskId) {
                    return {
                      ...t,
                      actualStart: t.actualStart || originalStart,
                      actualEnd: newEnd
                    };
                  }
                  return t;
                });
                if (dependencyCityId && cascadeEdgesRef.current.length) {
                  next = applyCascadeToTasks(next, cascadeEdgesRef.current, rs.taskId);
                }
                return next;
              });
            }
          } else {
            const newStart = new Date(rs.originalStartTime + delta * 24 * 60 * 60 * 1000);
            const originalEnd = new Date(rs.originalEndTime);
            
            if (newStart < originalEnd) {
              setTasks((prev) => {
                let next = prev.map((t) => {
                  if (t.id === rs.taskId) {
                    return {
                      ...t,
                      actualStart: newStart,
                      actualEnd: t.actualEnd || originalEnd
                    };
                  }
                  return t;
                });
                if (dependencyCityId && cascadeEdgesRef.current.length) {
                  next = applyCascadeToTasks(next, cascadeEdgesRef.current, rs.taskId);
                }
                return next;
              });
            }
          }
        }
      });
    }
  }, [DAY_WIDTH, dependencyCityId]);

  const handleMouseUp = useCallback(() => {
    const dragState = dragStateRef.current;
    const resizeState = resizeStateRef.current;
    const originalTask = originalTaskRef.current;
    const fullSnapshot = tasksSnapshotRef.current;

    // Очищаем refs и state СРАЗУ (до сохранения)
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    pendingUpdateRef.current = null;
    dragStateRef.current = null;
    resizeStateRef.current = null;
    originalTaskRef.current = null;
    tasksSnapshotRef.current = null;
    setDragging(null);
    setResizing(null);
    
    // Если не было перетаскивания - выходим
    if (!dragState && !resizeState) return;
    
    const taskId = dragState?.taskId || resizeState?.taskId;
    
    // Используем ref для доступа к текущим tasks (без зависимости в useCallback)
    const currentTasks = tasksRef.current;
    const updated = currentTasks.find(t => t.id === taskId);
    
    // Проверяем что задача существует и даты валидны
    if (!updated || !updated.actualStart || !updated.actualEnd) {
      console.warn('Задача не найдена или даты невалидны');
      return;
    }
    
    // Формируем данные для сохранения (YYYY-MM-DD в локальном времени)
    const saveData = {
      actual_start_date: formatDateForInput(updated.actualStart),
      actual_end_date: formatDateForInput(updated.actualEnd)
    };
    
    // КРИТИЧЕСКИЙ БАГ-ФИХ: Убираем setTimeout - вызываем сохранение сразу
    // Используем счётчик вместо boolean для поддержки множественных операций
    (async () => {
      // Увеличиваем счётчик активных операций
      setSyncCount(prev => prev + 1);
      
      try {
        if (onScheduleUpdate) {
          // БАГ-ФИХ: Добавляем таймаут для запроса (10 секунд)
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Таймаут сохранения')), 10000)
          );
          
          await Promise.race([
            onScheduleUpdate(updated.id, saveData),
            timeoutPromise
          ]);
        }
      } catch (error) {
        console.error('Ошибка сохранения:', error);

        if (fullSnapshot) {
          setTasks(
            fullSnapshot.map((t) => ({
              ...t,
              plannedStart: t.plannedStart ? new Date(t.plannedStart.getTime()) : null,
              plannedEnd: t.plannedEnd ? new Date(t.plannedEnd.getTime()) : null,
              actualStart: t.actualStart ? new Date(t.actualStart.getTime()) : null,
              actualEnd: t.actualEnd ? new Date(t.actualEnd.getTime()) : null
            }))
          );
        } else if (originalTask) {
          setTasks((prev) => prev.map((t) => (t.id === taskId ? originalTask : t)));
        }
      } finally {
        // БАГ-ФИХ: Уменьшаем счётчик активных операций
        setSyncCount(prev => Math.max(0, prev - 1));
      }
      
      // Обновляем критический путь в фоне (после завершения сохранения)
      if (showCriticalPath) {
        fetchDependencyGraph()
          .then(data => setCpmData(data))
          .catch(err => console.error('Ошибка обновления КП:', err));
      }
    })();
  }, [onScheduleUpdate, showCriticalPath, fetchDependencyGraph]);

  useEffect(() => {
    if (dragging || resizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      };
    }
  }, [dragging, resizing, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    return () => {
      setSyncCount(0);
    };
  }, []);

  const isCritical = (task) => {
    if (!showCriticalPath) return false;
    // Проверяем по ID задачи (если есть данные с сервера) или по этапу (fallback)
    if (cpmData.criticalTaskIds && cpmData.criticalTaskIds.length > 0) {
      return cpmData.criticalTaskIds.includes(task.id);
    }
    return Array.isArray(cpmData.criticalStages)
      && cpmData.criticalStages.includes(task.constructionStage);
  };
  
  // Возвращает объект { delay: число дней задержки, early: число дней раньше срока }
  const getTimingInfo = (task) => {
    const now = new Date();
    let delay = 0;
    let early = 0;
    
    // Проверяем задержку окончания
    if (task.actualEnd && task.actualEnd > task.plannedEnd) {
      delay = Math.ceil((task.actualEnd - task.plannedEnd) / (1000 * 60 * 60 * 24));
    } else if (!task.actualEnd && task.actualStart && now > task.plannedEnd) {
      delay = Math.ceil((now - task.plannedEnd) / (1000 * 60 * 60 * 24));
    }
    
    // Проверяем раннее завершение
    if (task.actualEnd && task.actualEnd < task.plannedEnd) {
      early = Math.ceil((task.plannedEnd - task.actualEnd) / (1000 * 60 * 60 * 24));
    }
    
    // Проверяем раннее начало
    let earlyStart = 0;
    let lateStart = 0;
    if (task.actualStart && task.actualStart < task.plannedStart) {
      earlyStart = Math.ceil((task.plannedStart - task.actualStart) / (1000 * 60 * 60 * 24));
    } else if (task.actualStart && task.actualStart > task.plannedStart) {
      lateStart = Math.ceil((task.actualStart - task.plannedStart) / (1000 * 60 * 60 * 24));
    }
    
    return { delay, early, earlyStart, lateStart };
  };

  const getStageFloat = (stage) => {
    const node = (cpmData.nodes || []).find(n => n.stage === stage);
    return node?.float || 0;
  };

  // Рендер бара
  const canDragGlobal = onScheduleUpdate && (!defaultDragDisabled || dragModeEnabled);
  const renderTaskBar = (task) => {
    const canDrag = canDragGlobal && !task.isUndated;
    const plannedPos = getPlannedPosition(task);
    const actualPos = getTaskPosition(task);
    const critical = isCritical(task);
    const timing = getTimingInfo(task);
    const stageFloat = getStageFloat(task.constructionStage);
    const isHovered = hoveredTask === task.id;
    
    // Есть ли фактические даты
    const hasActualDates = task.actualStart && task.actualEnd;
    
    // Цвет всегда соответствует отделу
    let bgColor = task.color.bg;
    let borderColor = task.color.border;
    
    if (critical) {
      bgColor = '#f97316';
      borderColor = '#c2410c';
    } else if (task.isUndated) {
      bgColor = `${task.color.bg}99`;
      borderColor = task.color.border;
    }

    const isOnTime = hasActualDates && timing.delay === 0 && timing.early === 0;

    // Позиция для отображения (фактические или плановые)
    const pos = hasActualDates ? actualPos : plannedPos;

    return (
      <div
        key={task.id}
        data-task-id={task.id}
        style={{
          position: 'absolute',
          left: Math.min(plannedPos.left, pos.left),
          width: Math.max(plannedPos.left + plannedPos.width, pos.left + pos.width) - Math.min(plannedPos.left, pos.left),
          top: (ROW_HEIGHT - TASK_HEIGHT) / 2,
          height: TASK_HEIGHT,
          opacity: showCriticalPath && !critical ? 0.35 : 1,
          zIndex: isHovered ? 10 : 1
        }}
        onMouseEnter={(e) => setTaskHover(task.id, e.currentTarget)}
        onMouseLeave={clearTaskHover}
      >
        {/* Плановые даты - серая полоска в фоне */}
        <div
          style={{
            position: 'absolute',
            left: plannedPos.left - Math.min(plannedPos.left, pos.left),
            width: plannedPos.width,
            height: '100%',
            background: 'repeating-linear-gradient(45deg, #e2e8f0, #e2e8f0 4px, #f1f5f9 4px, #f1f5f9 8px)',
            borderRadius: '4px',
            border: '1px dashed #94a3b8',
            opacity: hasActualDates ? 0.6 : 0,
            transition: dragging || resizing ? 'none' : 'opacity 0.3s ease'
          }}
        />
        
        {/* Основной бар: при наличии факта — сплошной цвет, иначе пунктирный план */}
        <div
          style={{
            position: 'absolute',
            left: pos.left - Math.min(plannedPos.left, pos.left),
            width: pos.width,
            height: '100%',
            background: hasActualDates
              ? `linear-gradient(180deg, ${bgColor} 0%, ${borderColor} 100%)`
              : 'repeating-linear-gradient(45deg, #e2e8f0, #e2e8f0 4px, #f1f5f9 4px, #f1f5f9 8px)',
            borderRadius: '4px',
            border: task.isUndated
              ? `2px dashed ${borderColor}`
              : hasActualDates
                ? (critical ? '2px solid #fff' : `1px solid ${borderColor}`)
                : '1px dashed #94a3b8',
            opacity: task.isUndated ? 0.85 : 1,
            boxShadow: hasActualDates && !task.isUndated && (isHovered ? '0 4px 12px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.2)'),
            cursor: canDrag ? (dragging || resizing ? 'grabbing' : 'grab') : 'pointer',
            transition: dragging || resizing ? 'none' : 'box-shadow 0.2s ease, opacity 0.2s ease',
            willChange: dragging || resizing ? 'transform' : 'auto'
          }}
          onMouseDown={(e) => handleMouseDown(e, task, 'move')}
          onClick={() => {
            if (barClickMovedRef.current) return;
            scrollToTask(task.id);
          }}
        >
          {task.isUndated && (
            <div style={{
              position: 'absolute',
              top: '-11px',
              right: '4px',
              background: '#64748b',
              color: '#fff',
              fontSize: '9px',
              fontWeight: 700,
              padding: '1px 6px',
              borderRadius: '6px',
              whiteSpace: 'nowrap',
              zIndex: 5,
            }}>
              без дат
            </div>
          )}
          {/* Содержимое бара */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '100%',
            padding: '0 6px',
            color: hasActualDates ? '#fff' : '#475569',
            fontSize: '10px',
            fontWeight: '600',
            overflow: 'hidden',
          }}>
            {/* Дата начала (фактическая или плановая) */}
            <span style={{ 
              background: hasActualDates ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.8)', 
              padding: '2px 4px', 
              borderRadius: '3px',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}>
              {formatDateShort(hasActualDates ? task.actualStart : task.plannedStart)}
            </span>
            
            {/* Этап строительства и название */}
            {pos.width > 80 && (
              <div style={{
                flex: 1,
                textAlign: 'center',
                overflow: 'hidden',
                padding: '0 4px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1px'
              }}>
                {/* Этап строительства - компактный бейдж */}
                <span style={{
                  fontSize: '8px',
                  fontWeight: '700',
                  background: hasActualDates ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.06)',
                  padding: '1px 4px',
                  borderRadius: '3px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px'
                }}>
                  {task.constructionStage}
                </span>
                {/* Название работы (если ширина позволяет) */}
                {pos.width > 160 && task.workName && (
                  <span style={{
                    fontSize: '10px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                    opacity: 0.9
                  }}>
                    {hasActualDates ? task.workName : '📋 План'}
                  </span>
                )}
                {/* Только для плана без названия */}
                {pos.width > 160 && !task.workName && !hasActualDates && (
                  <span style={{ fontSize: '10px', opacity: 0.8 }}>📋 План</span>
                )}
              </div>
            )}
            
            {/* Дата окончания (фактическая или плановая) */}
            <span style={{ 
              background: hasActualDates ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.8)', 
              padding: '2px 4px', 
              borderRadius: '3px',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}>
              {formatDateShort(hasActualDates ? task.actualEnd : task.plannedEnd)}
            </span>
          </div>

          {/* Маркер статуса */}
          {hasActualDates && (timing.early > 0 || timing.delay > 0 || timing.earlyStart > 0 || timing.lateStart > 0 || isOnTime) && (
            <div style={{
              position: 'absolute',
              top: '-12px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: '4px'
            }}>
              {isOnTime && (
                <span style={{
                  background: '#8bc49a',
                  color: '#fff',
                  fontSize: '9px',
                  fontWeight: 'bold',
                  padding: '2px 6px',
                  borderRadius: '8px',
                  whiteSpace: 'nowrap'
                }}>
                  ✓ В срок
                </span>
              )}
              {timing.early > 0 && (
                <span style={{
                  background: '#7ab5ad',
                  color: '#fff',
                  fontSize: '9px',
                  fontWeight: 'bold',
                  padding: '2px 6px',
                  borderRadius: '8px',
                  whiteSpace: 'nowrap'
                }}>
                  ✓ +{timing.early}д раньше
                </span>
              )}
              {timing.delay > 0 && (
                <span style={{
                  background: '#dc2626',
                  color: '#fff',
                  fontSize: '9px',
                  fontWeight: 'bold',
                  padding: '2px 6px',
                  borderRadius: '8px',
                  whiteSpace: 'nowrap'
                }}>
                  ⚠ -{timing.delay}д позже
                </span>
              )}
              {timing.earlyStart > 0 && (
                <span style={{
                  background: '#0891b2',
                  color: '#fff',
                  fontSize: '9px',
                  fontWeight: 'bold',
                  padding: '2px 6px',
                  borderRadius: '8px',
                  whiteSpace: 'nowrap'
                }}>
                  ▶ +{timing.earlyStart}д раньше
                </span>
              )}
              {timing.lateStart > 0 && (
                <span style={{
                  background: '#d97706',
                  color: '#fff',
                  fontSize: '9px',
                  fontWeight: 'bold',
                  padding: '2px 6px',
                  borderRadius: '8px',
                  whiteSpace: 'nowrap'
                }}>
                  ▶ -{timing.lateStart}д позже
                </span>
              )}
            </div>
          )}

          {/* Маркер критического пути */}
          {critical && timing.delay === 0 && timing.early === 0 && (
            <div style={{
              position: 'absolute',
              top: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#ea580c',
              color: '#fff',
              fontSize: '9px',
              fontWeight: 'bold',
              padding: '1px 6px',
              borderRadius: '8px',
              whiteSpace: 'nowrap'
            }}>
              🔥 КП
            </div>
          )}

          {/* Резерв времени */}
          {showCriticalPath && !critical && stageFloat > 0 && timing.early === 0 && (
            <div style={{
              position: 'absolute',
              bottom: '-10px',
              right: '4px',
              background: '#8bc49a',
              color: '#fff',
              fontSize: '9px',
              fontWeight: 'bold',
              padding: '1px 5px',
              borderRadius: '8px'
            }}>
              +{stageFloat}д резерв
            </div>
          )}

          {/* Ручки resize - только при включённом режиме перетаскивания */}
          {canDrag && (
            <>
              <div
                style={{
                  position: 'absolute', 
                  left: 0, 
                  top: 0, 
                  width: '8px', 
                  height: '100%',
                  cursor: 'ew-resize', 
                  borderRadius: '4px 0 0 4px',
                  zIndex: 10
                }}
                onMouseDown={(e) => handleMouseDown(e, task, 'resize-start')}
              />
              <div
                style={{
                  position: 'absolute', 
                  right: 0, 
                  top: 0, 
                  width: '8px', 
                  height: '100%',
                  cursor: 'ew-resize', 
                  borderRadius: '0 4px 4px 0',
                  zIndex: 10
                }}
                onMouseDown={(e) => handleMouseDown(e, task, 'resize-end')}
              />
            </>
          )}
        </div>

        {/* Фактическое выполнение — показываем только при заполненных обеих дат */}
        {task.actualStart && task.actualEnd && (
          <div style={{
            position: 'absolute',
            left: Math.max(0, Math.ceil((task.actualStart - task.plannedStart) / (1000 * 60 * 60 * 24)) * DAY_WIDTH),
            top: TASK_HEIGHT + 2,
            width: Math.ceil((task.actualEnd - task.actualStart) / (1000 * 60 * 60 * 24)) * DAY_WIDTH,
            height: '4px',
            background: timing.delay > 0 ? '#d4a0a0' : '#8bc49a',
            borderRadius: '2px'
          }} />
        )}

      </div>
    );
  };

  const hoveredTaskData = hoveredTask != null
    ? tasks.find((t) => t.id === hoveredTask)
    : null;

  const renderHoveredTaskTooltip = () => {
    if (!hoveredTaskData || !hoverAnchorRect) return null;

    const task = hoveredTaskData;
    const timing = getTimingInfo(task);
    const critical = isCritical(task);
    const tooltipWidth = 240;
    const centerX = hoverAnchorRect.left + hoverAnchorRect.width / 2;
    const clampedLeft = Math.max(
      8,
      Math.min(centerX - tooltipWidth / 2, window.innerWidth - tooltipWidth - 8)
    );
    const top = Math.min(hoverAnchorRect.bottom + 8, window.innerHeight - 8);

    return ReactDOM.createPortal(
      <div
        data-testid="gantt-task-hover-tooltip"
        style={{
          position: 'fixed',
          top,
          left: clampedLeft,
          background: '#1e293b',
          color: '#fff',
          padding: '10px 14px',
          borderRadius: '8px',
          fontSize: '11px',
          whiteSpace: 'nowrap',
          zIndex: 10000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          pointerEvents: 'none',
          minWidth: '220px',
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '6px',
          paddingBottom: '6px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
        >
          <span style={{
            background: task.color.bg,
            color: task.color.border,
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: '700',
          }}
          >
            {typeNames[task.type]}
          </span>
          <span style={{ fontWeight: '600', color: '#e2e8f0' }}>
            {task.constructionStage}
          </span>
        </div>

        {task.workName && (
          <div style={{ fontWeight: '500', marginBottom: '6px', color: '#fff' }}>
            📋 {task.workName}
          </div>
        )}

        <div style={{ color: '#94a3b8', fontSize: '10px', marginBottom: '4px' }}>
          🏙️ {task.cityName}
        </div>

        <div style={{ color: '#94a3b8' }}>
          📅 План: {formatDate(task.plannedStart)} — {formatDate(task.plannedEnd)}
          {task.isUndated ? ' (даты не заданы — показано условно)' : ''}
        </div>
        {task.actualStart && (
          <div style={{ color: timing.delay > 0 ? '#fca5a5' : '#86efac', marginTop: '2px' }}>
            ✓ Факт: {formatDate(task.actualStart)} — {task.actualEnd ? formatDate(task.actualEnd) : 'в работе'}
          </div>
        )}

        {(timing.early > 0 || timing.delay > 0 || timing.earlyStart > 0 || timing.lateStart > 0 || critical) && (
          <div style={{
            marginTop: '6px',
            paddingTop: '6px',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}
          >
            {timing.early > 0 && (
              <div style={{ color: '#86efac' }}>🎉 Завершено на {timing.early} дн. раньше!</div>
            )}
            {timing.delay > 0 && (
              <div style={{ color: '#fca5a5' }}>⚠️ Опоздание на {timing.delay} дн.</div>
            )}
            {timing.earlyStart > 0 && (
              <div style={{ color: '#67e8f9' }}>▶ Начато на {timing.earlyStart} дн. раньше</div>
            )}
            {timing.lateStart > 0 && (
              <div style={{ color: '#fcd34d' }}>▶ Начато на {timing.lateStart} дн. позже</div>
            )}
            {critical && <div style={{ color: '#fdba74' }}>🔥 Критический путь</div>}
          </div>
        )}
      </div>,
      document.body
    );
  };

  return (
    <div style={{ width: '100%', fontFamily: "'Inter', -apple-system, sans-serif" }}>
      {/* Заголовок */}
      <div style={{ 
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: isDark ? '#f8fafc' : '#1e293b' }}>
          📊 {chartTitle || 'Диаграмма Ганта'}
        </h2>
        
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {!selectedView && (
            <DepartmentsFilter
              options={DEPT_OPTIONS}
              selected={selectedDepartments}
              onChange={setSelectedDepartments}
              isDark={isDark}
            />
          )}

          {/* Масштаб диаграммы */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '6px 14px',
            background: isDark ? 'rgba(30, 41, 59, 0.8)' : '#f8fafc',
            borderRadius: '8px',
            border: `1px solid ${isDark ? 'rgba(148, 163, 184, 0.2)' : '#e2e8f0'}`
          }}>
            <span style={{ fontSize: '12px', color: isDark ? '#94a3b8' : '#64748b', whiteSpace: 'nowrap' }}>
              Масштаб:
            </span>
            <div style={{ display: 'flex', gap: '4px' }}>
              {SCALE_PRESETS.map(({ key, label, value, icon }) => {
                const isActive = Math.abs(zoomLevel - value) < 0.5;
                return (
                  <button
                    key={key}
                    onClick={() => setZoomLevel(value)}
                    title={`${label} (${value} px/день)`}
                    style={{
                      padding: '5px 10px',
                      border: 'none',
                      borderRadius: '6px',
                      background: isActive ? (isDark ? '#475569' : '#e2e8f0') : 'transparent',
                      boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: isActive ? '600' : '500',
                      color: isActive ? (isDark ? '#f8fafc' : '#1e293b') : (isDark ? '#94a3b8' : '#64748b'),
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.background = isDark ? 'rgba(71, 85, 105, 0.5)' : 'rgba(226, 232, 240, 0.8)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    {icon}
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '160px' }}>
              <button
                type="button"
                onClick={() => setZoomLevel(prev => Math.max(ZOOM_MIN, prev - 2))}
                disabled={zoomLevel <= ZOOM_MIN}
                title="Уменьшить масштаб"
                style={{
                  width: '28px',
                  height: '28px',
                  padding: 0,
                  border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                  borderRadius: '6px',
                  background: isDark ? '#1e293b' : '#fff',
                  color: zoomLevel <= ZOOM_MIN ? (isDark ? '#475569' : '#cbd5e1') : (isDark ? '#f8fafc' : '#1e293b'),
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: zoomLevel <= ZOOM_MIN ? 'not-allowed' : 'pointer',
                  lineHeight: 1,
                  opacity: zoomLevel <= ZOOM_MIN ? 0.5 : 1
                }}
              >
                −
              </button>
              <input
                type="range"
                data-testid="gantt-zoom"
                min={ZOOM_MIN}
                max={ZOOM_MAX}
                step="0.5"
                value={zoomLevel}
                onChange={(e) => setZoomLevel(parseFloat(e.target.value))}
                style={{
                  flex: 1,
                  height: '6px',
                  accentColor: isDark ? '#3B82F6' : '#2563eb',
                  cursor: 'pointer'
                }}
              />
              <button
                type="button"
                onClick={() => setZoomLevel(prev => Math.min(ZOOM_MAX, prev + 2))}
                disabled={zoomLevel >= ZOOM_MAX}
                title="Увеличить масштаб"
                style={{
                  width: '28px',
                  height: '28px',
                  padding: 0,
                  border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                  borderRadius: '6px',
                  background: isDark ? '#1e293b' : '#fff',
                  color: zoomLevel >= ZOOM_MAX ? (isDark ? '#475569' : '#cbd5e1') : (isDark ? '#f8fafc' : '#1e293b'),
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: zoomLevel >= ZOOM_MAX ? 'not-allowed' : 'pointer',
                  lineHeight: 1,
                  opacity: zoomLevel >= ZOOM_MAX ? 0.5 : 1
                }}
              >
                +
              </button>
              <span style={{
                fontSize: '11px',
                color: isDark ? '#64748b' : '#94a3b8',
                minWidth: '40px'
              }}>
                {zoomLevel >= 10 ? Math.round(zoomLevel) : zoomLevel.toFixed(1)} px/д
              </span>
            </div>
          </div>

          {/* Кнопка включения перетаскивания (только для сводной диаграммы) */}
          {onScheduleUpdate && defaultDragDisabled && (
            <button
              onClick={() => setDragModeEnabled(prev => !prev)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                background: dragModeEnabled ? (isDark ? '#475569' : '#e2e8f0') : (isDark ? '#1e293b' : '#fff'),
                color: isDark ? '#f8fafc' : '#1e293b',
                fontSize: '12px',
                fontWeight: dragModeEnabled ? '600' : '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              title={dragModeEnabled ? 'Режим перетаскивания включён' : 'Нажмите для включения перетаскивания блоков'}
            >
              <span>{dragModeEnabled ? '✋' : '👆'}</span>
              {dragModeEnabled ? 'Режим перетаскивания' : 'Включить перетаскивание'}
            </button>
          )}

          {/* Сортировка */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: isDark ? '#94a3b8' : '#64748b' }}>Сортировка:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                background: isDark ? '#1e293b' : '#fff',
                fontSize: '13px',
                color: isDark ? '#f8fafc' : '#1e293b',
                cursor: 'pointer'
              }}
            >
              <option value="date">📅 По дате (сначала ранние)</option>
              <option value="date-desc">📅 По дате (сначала поздние)</option>
              <option value="stage">🏗️ По этапу</option>
              <option value="type">📁 По отделу</option>
              <option value="city">🏙️ По объекту</option>
              <option value="duration">⏱️ По длительности</option>
            </select>
          </div>

          {/* Фильтр по году */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            padding: '8px 16px',
            background: isDark ? 'rgba(30, 41, 59, 0.5)' : '#f8fafc',
            borderRadius: '8px',
            border: `1px solid ${isDark ? 'rgba(148, 163, 184, 0.2)' : '#e2e8f0'}`
          }}>
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: `1px solid ${isDark ? 'rgba(148, 163, 184, 0.3)' : '#e2e8f0'}`,
                background: isDark ? 'rgba(30, 41, 59, 0.8)' : '#fff',
                fontSize: '13px',
                color: isDark ? '#e2e8f0' : '#1e293b',
                cursor: 'pointer',
                minWidth: '120px'
              }}
            >
              <option value="all">📅 Все годы</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>

          </div>

          <button
            onClick={() => setShowCriticalPath(!showCriticalPath)}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              background: showCriticalPath ? '#d4a080' : '#7b9eb8',
              color: '#fff',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {showCriticalPath ? '🔥 Скрыть КП' : '⚡ Критический путь'}
          </button>
        </div>
        {hiddenLinkCount > 0 && (
          <div
            data-testid="gantt-hidden-links"
            style={{
              marginTop: 8,
              fontSize: 12,
              color: isDark ? '#94a3b8' : '#64748b',
            }}
            title="Связи с задачами других отделов или вне текущего списка не отображаются стрелками на этом графике"
          >
            Скрыто связей с другими отделами: {hiddenLinkCount}
            {' · '}
            <a href="/dependency-manager" style={{ color: '#ea580c', textDecoration: 'none' }}>
              Открыть зависимости
            </a>
          </div>
        )}
      </div>

      {/* Панель фильтров - годы и этапы */}
      <div style={{
        display: 'flex',
        gap: '24px',
        marginBottom: '16px',
        padding: '16px 20px',
        background: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>

        {/* Слайдер диапазона годов */}
        {yearFilter === 'all' && availableYears.length > 0 && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '6px',
            minWidth: 220
          }}>
            <div style={{ 
              fontSize: 11, 
              fontWeight: 600, 
              color: isDark ? '#94a3b8' : '#64748b', 
              textAlign: 'center' 
            }}>
              {activeStartYear} — {activeEndYear}
            </div>
            
            {/* Начальный год */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <label style={{ 
                fontSize: 9, 
                color: isDark ? '#64748b' : '#94a3b8', 
                textAlign: 'left' 
              }}>
                Начало: {activeStartYear}
              </label>
              <input
                type="range"
                min={yearRangeLimits.min}
                max={yearRangeLimits.max}
                value={activeStartYear}
                onChange={(e) => {
                  const next = Math.min(parseInt(e.target.value, 10), activeEndYear);
                  setStartYear(next);
                }}
                style={{
                  width: '100%',
                  height: 4,
                  borderRadius: 999,
                  appearance: 'none',
                  background: `linear-gradient(to right, 
                    ${isDark ? 'rgba(148, 163, 184, 0.25)' : 'rgba(148, 163, 184, 0.3)'} 0%, 
                    ${isDark ? 'rgba(148, 163, 184, 0.25)' : 'rgba(148, 163, 184, 0.3)'} ${((activeStartYear - yearRangeLimits.min) / (yearRangeLimits.max - yearRangeLimits.min)) * 100}%, 
                    ${isDark ? 'rgba(59, 130, 246, 0.8)' : 'rgba(59, 130, 246, 0.6)'} ${((activeStartYear - yearRangeLimits.min) / (yearRangeLimits.max - yearRangeLimits.min)) * 100}%, 
                    ${isDark ? 'rgba(59, 130, 246, 0.8)' : 'rgba(59, 130, 246, 0.6)'} 100%)`,
                  cursor: 'pointer'
                }}
              />
            </div>
            
            {/* Конечный год */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <label style={{ 
                fontSize: 9, 
                color: isDark ? '#64748b' : '#94a3b8', 
                textAlign: 'left' 
              }}>
                Конец: {activeEndYear}
              </label>
              <input
                type="range"
                min={yearRangeLimits.min}
                max={yearRangeLimits.max}
                value={activeEndYear}
                onChange={(e) => {
                  const next = Math.max(parseInt(e.target.value, 10), activeStartYear);
                  setEndYear(next);
                }}
                style={{
                  width: '100%',
                  height: 4,
                  borderRadius: 999,
                  appearance: 'none',
                  background: `linear-gradient(to right, 
                    ${isDark ? 'rgba(148, 163, 184, 0.25)' : 'rgba(148, 163, 184, 0.3)'} 0%, 
                    ${isDark ? 'rgba(148, 163, 184, 0.25)' : 'rgba(148, 163, 184, 0.3)'} ${((activeEndYear - yearRangeLimits.min) / (yearRangeLimits.max - yearRangeLimits.min)) * 100}%, 
                    ${isDark ? 'rgba(59, 130, 246, 0.8)' : 'rgba(59, 130, 246, 0.6)'} ${((activeEndYear - yearRangeLimits.min) / (yearRangeLimits.max - yearRangeLimits.min)) * 100}%, 
                    ${isDark ? 'rgba(59, 130, 246, 0.8)' : 'rgba(59, 130, 246, 0.6)'} 100%)`,
                  cursor: 'pointer'
                }}
              />
            </div>
          </div>
        )}

        {/* Фильтр по этапу строительства */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: '250px' }}>
          <span style={{ 
            fontSize: '12px', 
            fontWeight: '600', 
            color: '#64748b',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            whiteSpace: 'nowrap'
          }}>
            🏗️ Этап:
          </span>
          <select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: stageFilter ? '#eff6ff' : '#fff',
              fontSize: '13px',
              color: '#1e293b',
              cursor: 'pointer',
              flex: 1,
              maxWidth: '350px',
              fontWeight: stageFilter ? '500' : 'normal'
            }}
          >
            <option value="">Все этапы</option>
            {stages.map((stage) => (
              <option key={stage.id} value={stage.name}>
                {stage.order_index + 1}. {stage.name}
              </option>
            ))}
          </select>
        </div>

        {/* Счётчик и сброс */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ 
            fontSize: '12px', 
            color: '#64748b',
            background: '#e2e8f0',
            padding: '4px 10px',
            borderRadius: '12px'
          }}>
            Показано: <strong style={{ color: '#1e293b' }}>{filteredTasks.length}</strong> / {tasks.length}
          </span>
          
          {(stageFilter || (startYear !== null && endYear !== null && availableYears.length > 0 && (activeStartYear !== availableYears[0] || activeEndYear !== availableYears[availableYears.length - 1]))) && (
            <button
              onClick={() => {
                setStageFilter('');
                if (availableYears.length > 0) {
                  setStartYear(availableYears[0]);
                  setEndYear(availableYears[availableYears.length - 1]);
                }
              }}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: '6px',
                background: '#fee2e2',
                color: '#dc2626',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.15s ease'
              }}
            >
              ✕ Сбросить
            </button>
          )}
        </div>
      </div>

      {/* Стили для слайдеров */}
      <style>{`
        .year-range-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          background: #fff;
          border: 2px solid #3b82f6;
          border-radius: 50%;
          cursor: pointer;
          pointer-events: auto;
          box-shadow: 0 2px 6px rgba(59, 130, 246, 0.3);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .year-range-slider::-webkit-slider-thumb:hover {
          transform: scale(1.15);
          box-shadow: 0 3px 8px rgba(59, 130, 246, 0.4);
        }
        .year-range-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          background: #fff;
          border: 2px solid #3b82f6;
          border-radius: 50%;
          cursor: pointer;
          pointer-events: auto;
          box-shadow: 0 2px 6px rgba(59, 130, 246, 0.3);
        }
        .year-range-slider::-webkit-slider-runnable-track {
          height: 6px;
          background: transparent;
        }
        .year-range-slider::-moz-range-track {
          height: 6px;
          background: transparent;
        }
      `}</style>

      {/* Информация о КП */}
      {showCriticalPath && (cpmData.criticalTaskIds?.length > 0 || cpmData.criticalStages?.length > 0) && (
        <div style={{
          background: '#fff7ed',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
          border: '1px solid #fed7aa',
          display: 'flex',
          gap: '24px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '24px' }}>🔥</span>
            <span style={{ fontWeight: '600', color: '#c2410c' }}>Критический путь</span>
          </div>
          {cpmData.nodes?.length > 0 && (
            <div>
              <span style={{ color: '#78716c', fontSize: '12px' }}>Длительность: </span>
              <strong>{Math.max(...cpmData.nodes.map(n => n.EF || 0))} дней</strong>
            </div>
          )}
          <div>
            <span style={{ color: '#78716c', fontSize: '12px' }}>Критических задач: </span>
            <strong style={{ color: '#ea580c' }}>
              {cpmData.criticalTaskIds?.length || cpmData.criticalStages?.length || 0}
            </strong>
            <span style={{ color: '#a8a29e' }}> / {filteredTasks.length}</span>
          </div>
          {cpmData.nodes?.length > 0 && (
            <div>
              <span style={{ color: '#78716c', fontSize: '12px' }}>С резервом: </span>
              <strong style={{ color: '#16a34a' }}>{cpmData.nodes.filter(n => n.float > 0).length}</strong>
            </div>
          )}
          <div style={{ 
            marginLeft: 'auto', 
            fontSize: '12px', 
            color: '#78716c',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <a href="/dependency-manager" style={{ color: '#ea580c', textDecoration: 'none' }}>
              🔗 Настроить связи
            </a>
          </div>
        </div>
      )}

      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '8px'
      }}>
        <button
          type="button"
          data-testid="gantt-scroll-today"
          onClick={scrollToToday}
          disabled={todayOffset == null}
          title={todayOffset == null ? 'Сегодня вне диапазона диаграммы' : 'Прокрутить к сегодняшней дате'}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
            background: todayOffset == null
              ? (isDark ? '#1e293b' : '#fff')
              : (isDark ? '#334155' : '#fff'),
            color: todayOffset == null
              ? (isDark ? '#475569' : '#cbd5e1')
              : (isDark ? '#f8fafc' : '#1e293b'),
            fontSize: '13px',
            fontWeight: '600',
            cursor: todayOffset == null ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            opacity: todayOffset == null ? 0.5 : 1,
            transition: 'transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease'
          }}
          onMouseEnter={(e) => {
            if (todayOffset == null) return;
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = isDark
              ? '0 4px 12px rgba(0,0,0,0.35)'
              : '0 4px 12px rgba(15, 23, 42, 0.12)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'none';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          📅 Сегодня
        </button>
      </div>

      {/* Диаграмма */}
      <div style={{
        display: 'flex',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
        background: isDark ? '#1e293b' : '#fff',
        borderRadius: '8px',
        border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
        position: 'relative'
      }}>
        {/* Сайдбар */}
        <div style={{ width: SIDEBAR_WIDTH, flexShrink: 0, borderRight: `1px solid ${isDark ? '#334155' : '#e2e8f0'}` }}>
          {/* Заголовок сайдбара */}
          <div style={{
            height: HEADER_HEIGHT,
            background: isDark ? '#334155' : '#f8fafc',
            borderBottom: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
            display: 'flex',
            alignItems: 'center',
            padding: '0 12px',
            fontWeight: '600',
            color: isDark ? '#94a3b8' : '#475569',
            fontSize: '13px',
            position: 'relative',
            zIndex: 50
          }}>
            Задачи ({filteredTasks.length})
          </div>
          
          {/* Список задач */}
          <div 
            ref={sidebarRef}
            onScroll={handleSidebarScroll}
            style={{ 
              height: `calc(100vh - 400px)`,
              minHeight: '300px',
              maxHeight: '600px',
              overflowY: 'auto',
              overflowX: 'hidden'
            }}
          >
            {filteredTasks.map((task) => (
              <div
                key={task.id}
                role="button"
                tabIndex={0}
                style={{
                  height: ROW_HEIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 12px',
                  borderBottom: `1px solid ${isDark ? '#334155' : '#f1f5f9'}`,
                  background: hoveredTask === task.id ? (isDark ? '#334155' : '#f8fafc') : (isDark ? '#1e293b' : '#fff'),
                  cursor: 'pointer'
                }}
                onClick={() => scrollToTask(task.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    scrollToTask(task.id);
                  }
                }}
                onMouseEnter={(e) => setTaskHover(task.id, e.currentTarget)}
                onMouseLeave={clearTaskHover}
              >
                <div style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: isCritical(task) ? '#f97316' : task.color.bg,
                  marginRight: '10px',
                  flexShrink: 0,
                  border: isCritical(task) ? '2px solid #fff' : (isDark ? '1px solid rgba(255,255,255,0.35)' : 'none'),
                  boxShadow: isCritical(task) ? '0 0 0 2px #f97316' : (isDark ? '0 0 0 1px rgba(148,163,184,0.3)' : 'none')
                }} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  {/* Этап строительства - всегда показываем */}
                  <div style={{
                    fontSize: '10px',
                    fontWeight: '600',
                    color: task.color.border,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    marginBottom: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <span style={{
                      background: task.color.bg,
                      padding: '1px 5px',
                      borderRadius: '4px',
                      fontSize: '9px'
                    }}>
                      {typeNames[task.type]}
                    </span>
                    <span style={{ color: isDark ? '#94a3b8' : '#64748b' }}>•</span>
                    <span>{task.constructionStage}</span>
                  </div>
                  {/* Название работы */}
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: isDark ? '#f8fafc' : '#1e293b',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {task.workName || '—'}
                  </div>
                  {/* Даты */}
                  <div style={{
                    fontSize: '9px',
                    color: isDark ? '#94a3b8' : '#64748b',
                    display: 'flex',
                    gap: '4px',
                    marginTop: '1px'
                  }}>
                    {task.actualStart ? (
                      <span style={{ color: '#8bc49a' }}>
                        ✓ {formatDateShort(task.actualStart)} - {formatDateShort(task.actualEnd || task.plannedEnd)}
                      </span>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>
                        📋 {formatDateShort(task.plannedStart)} - {formatDateShort(task.plannedEnd)}
                      </span>
                    )}
                  </div>
                </div>
                {isCritical(task) && <span style={{ fontSize: '12px' }}>🔥</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Таймлайн */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Заголовок таймлайна */}
          <div 
            ref={timelineHeaderRef}
            onScroll={handleTimelineHeaderScroll}
            style={{
              height: HEADER_HEIGHT,
              background: isDark ? '#1e293b' : '#f8fafc',
              borderBottom: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
              overflowX: 'auto',
              overflowY: 'hidden',
              position: 'relative',
              zIndex: 50
            }}
          >
            <div style={{ display: 'flex', minWidth: timeRange.days * DAY_WIDTH }}>
              {monthHeaders.map((month, i) => (
                <div
                  key={i}
                  style={{
                    width: month.width,
                    flexShrink: 0,
                    height: HEADER_HEIGHT,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRight: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                    fontWeight: '600',
                    fontSize: '12px',
                    color: isDark ? '#94a3b8' : '#475569'
                  }}
                >
                  {month.label}
                </div>
              ))}
            </div>
          </div>

          {/* Область задач */}
          <div 
            ref={timelineBodyRef}
            onScroll={handleTimelineScroll}
            style={{ 
              height: `calc(100vh - 400px)`,
              minHeight: '300px',
              maxHeight: '600px',
              overflowY: 'auto',
              overflowX: 'auto'
            }}
          >
            <div style={{ 
              position: 'relative',
              minWidth: timeRange.days * DAY_WIDTH,
              minHeight: tasks.length * ROW_HEIGHT
            }}>
              {/* Сетка */}
              <div style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundImage: `repeating-linear-gradient(90deg, ${isDark ? '#334155' : '#f1f5f9'} 0px, ${isDark ? '#334155' : '#f1f5f9'} 1px, transparent 1px, transparent ${DAY_WIDTH * 7}px)`,
                pointerEvents: 'none'
              }} />

              {/* SVG слой для отрисовки связей — всегда, если есть рёбра */}
              {cpmData.edges && cpmData.edges.length > 0 && (
                <svg 
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 2
                  }}
                >
                  <defs>
                    {/* Фильтр тени для критических стрелок */}
                    <filter id="arrow-shadow" x="-50%" y="-50%" width="200%" height="200%">
                      <feDropShadow dx="2" dy="2" stdDeviation="2" floodColor="#DC2626" floodOpacity="0.4"/>
                    </filter>
                    {/* Фильтр свечения для критического пути */}
                    <filter id="critical-glow" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                    
                    {/* Цвета для типов связей */}
                    {/* FS - Finish-to-Start: синий (обычный) / красный (критический) */}
                    {/* SS - Start-to-Start: зелёный */}
                    {/* FF - Finish-to-Finish: оранжевый */}
                    {/* SF - Start-to-Finish: фиолетовый */}
                    
                    {/* Стрелки для каждого типа связи */}
                    <marker id="arrow-FS" markerWidth="12" markerHeight="10" refX="10" refY="5" orient="auto" markerUnits="userSpaceOnUse">
                      <path d="M 0 0 L 10 5 L 0 10 L 3 5 Z" fill="#3B82F6" />
                    </marker>
                    <marker id="arrow-SS" markerWidth="12" markerHeight="10" refX="10" refY="5" orient="auto" markerUnits="userSpaceOnUse">
                      <path d="M 0 0 L 10 5 L 0 10 L 3 5 Z" fill="#10B981" />
                    </marker>
                    <marker id="arrow-FF" markerWidth="12" markerHeight="10" refX="10" refY="5" orient="auto" markerUnits="userSpaceOnUse">
                      <path d="M 0 0 L 10 5 L 0 10 L 3 5 Z" fill="#F59E0B" />
                    </marker>
                    <marker id="arrow-SF" markerWidth="12" markerHeight="10" refX="10" refY="5" orient="auto" markerUnits="userSpaceOnUse">
                      <path d="M 0 0 L 10 5 L 0 10 L 3 5 Z" fill="#8B5CF6" />
                    </marker>
                    {/* Критическая стрелка - яркий красный */}
                    <marker id="arrow-critical" markerWidth="14" markerHeight="12" refX="12" refY="6" orient="auto" markerUnits="userSpaceOnUse">
                      <path d="M 0 0 L 12 6 L 0 12 L 4 6 Z" fill="#DC2626" />
                    </marker>
                  </defs>
                  {cpmData.edges.map(edge => {
                    const sourceTask = tasks.find(t => t.id === edge.source);
                    const targetTask = tasks.find(t => t.id === edge.target);
                    
                    if (!sourceTask || !targetTask) {
                      return null;
                    }
                    
                    const sourceIndex = tasks.indexOf(sourceTask);
                    const targetIndex = tasks.indexOf(targetTask);
                    
                    if (sourceIndex === -1 || targetIndex === -1) return null;
                    
                    // Используем фактические даты если есть, иначе плановые
                    const sourceEnd = sourceTask.actualEnd || sourceTask.plannedEnd;
                    const sourceStart = sourceTask.actualStart || sourceTask.plannedStart;
                    const targetStart = targetTask.actualStart || targetTask.plannedStart;
                    const targetEnd = targetTask.actualEnd || targetTask.plannedEnd;
                    
                    // Определяем точки подключения в зависимости от типа связи
                    let sourceX, targetX;
                    const linkType = edge.link_type || 'FS';
                    
                    switch (linkType) {
                      case 'SS': // Start-to-Start
                        sourceX = Math.ceil((sourceStart - timeRange.start) / (1000 * 60 * 60 * 24)) * DAY_WIDTH;
                        targetX = Math.ceil((targetStart - timeRange.start) / (1000 * 60 * 60 * 24)) * DAY_WIDTH;
                        break;
                      case 'FF': // Finish-to-Finish
                        sourceX = Math.ceil((sourceEnd - timeRange.start) / (1000 * 60 * 60 * 24)) * DAY_WIDTH;
                        targetX = Math.ceil((targetEnd - timeRange.start) / (1000 * 60 * 60 * 24)) * DAY_WIDTH;
                        break;
                      case 'SF': // Start-to-Finish
                        sourceX = Math.ceil((sourceStart - timeRange.start) / (1000 * 60 * 60 * 24)) * DAY_WIDTH;
                        targetX = Math.ceil((targetEnd - timeRange.start) / (1000 * 60 * 60 * 24)) * DAY_WIDTH;
                        break;
                      default: // FS - Finish-to-Start
                        sourceX = Math.ceil((sourceEnd - timeRange.start) / (1000 * 60 * 60 * 24)) * DAY_WIDTH;
                        targetX = Math.ceil((targetStart - timeRange.start) / (1000 * 60 * 60 * 24)) * DAY_WIDTH;
                    }
                    
                    const sourceY = sourceIndex * ROW_HEIGHT + ROW_HEIGHT / 2;
                    const targetY = targetIndex * ROW_HEIGHT + ROW_HEIGHT / 2;
                    
                    // Проверка на критический путь
                    const isCriticalLink = cpmData.criticalTaskIds?.includes(edge.source) && 
                                          cpmData.criticalTaskIds?.includes(edge.target);
                    
                    // Цвета для типов связей
                    const linkColors = {
                      FS: { color: '#3B82F6', name: 'Конец → Начало' },
                      SS: { color: '#10B981', name: 'Начало → Начало' },
                      FF: { color: '#F59E0B', name: 'Конец → Конец' },
                      SF: { color: '#8B5CF6', name: 'Начало → Конец' }
                    };
                    
                    const linkColor = isCriticalLink ? '#DC2626' : linkColors[linkType]?.color || '#3B82F6';
                    const arrowId = isCriticalLink ? 'arrow-critical' : `arrow-${linkType}`;
                    
                    // Улучшенное формирование пути
                    const dx = targetX - sourceX;
                    const dy = targetY - sourceY;
                    const isGoingBack = dx < 0;
                    
                    let pathD;
                    const startOffset = linkType === 'SS' || linkType === 'SF' ? -4 : 4;
                    const endOffset = linkType === 'FF' || linkType === 'SF' ? 4 : -14;
                    
                    if (isGoingBack || Math.abs(dx) < 30) {
                      // Если цель левее источника или очень близко - обходим
                      const offsetY = dy >= 0 ? 25 : -25;
                      const bendX = Math.max(sourceX, targetX) + 30;
                      pathD = `M ${sourceX + startOffset} ${sourceY} 
                               L ${bendX} ${sourceY}
                               Q ${bendX + 10} ${sourceY}, ${bendX + 10} ${sourceY + offsetY * 0.5}
                               L ${bendX + 10} ${targetY - offsetY * 0.5}
                               Q ${bendX + 10} ${targetY}, ${bendX} ${targetY}
                               L ${targetX + endOffset} ${targetY}`;
                    } else if (Math.abs(dy) < 5) {
                      // Горизонтальная линия с небольшим изгибом
                      pathD = `M ${sourceX + startOffset} ${sourceY} 
                               C ${sourceX + dx * 0.3} ${sourceY - 15}, 
                                 ${sourceX + dx * 0.7} ${targetY - 15}, 
                                 ${targetX + endOffset} ${targetY}`;
                    } else {
                      // Красивая кривая Безье
                      const ctrlOffset = Math.min(Math.abs(dx) * 0.4, 80);
                      pathD = `M ${sourceX + startOffset} ${sourceY} 
                               C ${sourceX + ctrlOffset} ${sourceY}, 
                                 ${targetX - ctrlOffset} ${targetY}, 
                                 ${targetX + endOffset} ${targetY}`;
                    }
                    
                    return (
                      <g key={edge.id}>
                        {/* Фоновое свечение для критического пути */}
                        {isCriticalLink && (
                          <path
                            d={pathD}
                            fill="none"
                            stroke="#DC2626"
                            strokeWidth={12}
                            strokeLinecap="round"
                            opacity={0.15}
                          />
                        )}
                        {/* Белая обводка для контраста */}
                        <path
                          d={pathD}
                          fill="none"
                          stroke="white"
                          strokeWidth={isCriticalLink ? 6 : 4}
                          strokeLinecap="round"
                        />
                        {/* Основная линия */}
                        <path
                          d={pathD}
                          fill="none"
                          stroke={linkColor}
                          strokeWidth={isCriticalLink ? 4 : 2.5}
                          strokeLinecap="round"
                          strokeDasharray={linkType !== 'FS' ? '8,4' : 'none'}
                          markerEnd={`url(#${arrowId})`}
                          style={{
                            filter: isCriticalLink ? 'url(#critical-glow)' : 'none'
                          }}
                        />
                        {/* Точка начала связи */}
                        <circle
                          cx={sourceX + startOffset}
                          cy={sourceY}
                          r={isCriticalLink ? 5 : 4}
                          fill={linkColor}
                          stroke="white"
                          strokeWidth={2}
                        />
                        {/* Метка типа связи */}
                        <g>
                          <rect
                            x={(sourceX + targetX) / 2 - 22}
                            y={(sourceY + targetY) / 2 - 11}
                            width={44}
                            height={22}
                            rx={6}
                            fill="white"
                            stroke={linkColor}
                            strokeWidth={isCriticalLink ? 2 : 1.5}
                            style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}
                          />
                          <text
                            x={(sourceX + targetX) / 2}
                            y={(sourceY + targetY) / 2 + 5}
                            textAnchor="middle"
                            fontSize="12"
                            fill={linkColor}
                            fontWeight="700"
                          >
                            {linkType}{edge.lag_days > 0 && `+${edge.lag_days}`}
                          </text>
                        </g>
                        {/* Индикатор критического пути */}
                        {isCriticalLink && (
                          <g>
                            <circle
                              cx={(sourceX + targetX) / 2}
                              cy={(sourceY + targetY) / 2 - 18}
                              r={8}
                              fill="#DC2626"
                            />
                            <text
                              x={(sourceX + targetX) / 2}
                              y={(sourceY + targetY) / 2 - 14}
                              textAnchor="middle"
                              fontSize="10"
                              fill="white"
                              fontWeight="700"
                            >
                              !
                            </text>
                          </g>
                        )}
                      </g>
                    );
                  })}
                </svg>
              )}

              {/* Линия сегодня */}
              {todayOffset != null && (
                <div style={{
                  position: 'absolute',
                  left: todayOffset,
                  top: 0,
                  bottom: 0,
                  width: todayPulse ? '3px' : '2px',
                  background: '#c4a08b',
                  zIndex: 5,
                  boxShadow: todayPulse ? '0 0 0 6px rgba(196, 160, 139, 0.28)' : 'none',
                  transition: 'width 0.2s ease, box-shadow 0.35s ease'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '4px',
                    left: '50%',
                    transform: todayPulse ? 'translateX(-50%) scale(1.08)' : 'translateX(-50%)',
                    background: '#c4a08b',
                    color: '#fff',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '9px',
                    fontWeight: '600',
                    transition: 'transform 0.35s ease'
                  }}>
                    Сегодня
                  </div>
                </div>
              )}

              {/* Строки задач */}
              {filteredTasks.map((task, index) => (
                <div
                  key={task.id}
                  style={{
                    height: ROW_HEIGHT,
                    position: 'relative',
                    borderBottom: '1px solid #f1f5f9',
                    background: hoveredTask === task.id ? 'rgba(59, 130, 246, 0.03)' : 'transparent'
                  }}
                >
                  {renderTaskBar(task)}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Легенда */}
      <div style={{
        marginTop: '16px',
        padding: '16px',
        background: '#f8fafc',
        borderRadius: '8px',
        fontSize: '12px'
      }}>
        {/* Отделы */}
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
          <span style={{ fontWeight: '600', color: '#475569' }}>Отделы:</span>
          {Object.entries(typeColors).map(([type, colors]) => (
            <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '20px', height: '10px', borderRadius: '2px',
                background: `linear-gradient(180deg, ${colors.bg} 0%, ${colors.border} 100%)`
              }} />
              <span style={{ color: '#64748b' }}>{typeNames[type]}</span>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ 
              width: '20px', height: '10px', borderRadius: '2px', 
              background: 'repeating-linear-gradient(45deg, #e2e8f0, #e2e8f0 2px, #f1f5f9 2px, #f1f5f9 4px)',
              border: '1px dashed #94a3b8'
            }} />
            <span style={{ color: '#64748b' }}>План (фон)</span>
          </div>
        </div>
        
        {/* Типы связей — показываем при наличии рёбер */}
        {cpmData.edges?.length > 0 && (
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            flexWrap: 'wrap', 
            paddingTop: '12px',
            borderTop: '1px solid #e2e8f0'
          }}>
            <span style={{ fontWeight: '600', color: '#475569' }}>Связи задач:</span>
            
            {/* Критический путь */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              padding: '4px 10px',
              background: '#FEE2E2',
              borderRadius: '6px',
              border: '1px solid #DC2626'
            }}>
              <svg width="28" height="12" viewBox="0 0 28 12">
                <circle cx="4" cy="6" r="3" fill="#DC2626" stroke="white" strokeWidth="1"/>
                <path d="M 7 6 Q 14 2, 20 6" stroke="#DC2626" strokeWidth="3" fill="none"/>
                <path d="M 18 3 L 24 6 L 18 9 L 20 6 Z" fill="#DC2626"/>
              </svg>
              <span style={{ color: '#DC2626', fontWeight: '600' }}>🔥 КРИТИЧЕСКИЙ ПУТЬ</span>
            </div>
            
            {/* FS - Finish to Start */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="28" height="12" viewBox="0 0 28 12">
                <circle cx="4" cy="6" r="2.5" fill="#3B82F6" stroke="white" strokeWidth="1"/>
                <path d="M 6 6 Q 14 3, 20 6" stroke="#3B82F6" strokeWidth="2" fill="none"/>
                <path d="M 18 3 L 24 6 L 18 9 L 20 6 Z" fill="#3B82F6"/>
              </svg>
              <span style={{ color: '#3B82F6', fontWeight: '600' }}>FS</span>
              <span style={{ color: '#64748b', fontSize: '11px' }}>Конец→Начало</span>
            </div>
            
            {/* SS - Start to Start */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="28" height="12" viewBox="0 0 28 12">
                <circle cx="4" cy="6" r="2.5" fill="#10B981" stroke="white" strokeWidth="1"/>
                <path d="M 6 6 Q 14 3, 20 6" stroke="#10B981" strokeWidth="2" fill="none" strokeDasharray="4,2"/>
                <path d="M 18 3 L 24 6 L 18 9 L 20 6 Z" fill="#10B981"/>
              </svg>
              <span style={{ color: '#10B981', fontWeight: '600' }}>SS</span>
              <span style={{ color: '#64748b', fontSize: '11px' }}>Начало→Начало</span>
            </div>
            
            {/* FF - Finish to Finish */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="28" height="12" viewBox="0 0 28 12">
                <circle cx="4" cy="6" r="2.5" fill="#F59E0B" stroke="white" strokeWidth="1"/>
                <path d="M 6 6 Q 14 3, 20 6" stroke="#F59E0B" strokeWidth="2" fill="none" strokeDasharray="4,2"/>
                <path d="M 18 3 L 24 6 L 18 9 L 20 6 Z" fill="#F59E0B"/>
              </svg>
              <span style={{ color: '#F59E0B', fontWeight: '600' }}>FF</span>
              <span style={{ color: '#64748b', fontSize: '11px' }}>Конец→Конец</span>
            </div>
            
            {/* SF - Start to Finish */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="28" height="12" viewBox="0 0 28 12">
                <circle cx="4" cy="6" r="2.5" fill="#8B5CF6" stroke="white" strokeWidth="1"/>
                <path d="M 6 6 Q 14 3, 20 6" stroke="#8B5CF6" strokeWidth="2" fill="none" strokeDasharray="4,2"/>
                <path d="M 18 3 L 24 6 L 18 9 L 20 6 Z" fill="#8B5CF6"/>
              </svg>
              <span style={{ color: '#8B5CF6', fontWeight: '600' }}>SF</span>
              <span style={{ color: '#64748b', fontSize: '11px' }}>Начало→Конец</span>
            </div>
          </div>
        )}
      </div>

      <div style={{ 
        marginTop: '12px', 
        padding: '10px 14px',
        fontSize: '12px', 
        color: isDark ? '#94a3b8' : '#64748b',
        background: isDark ? '#334155' : '#f1f5f9',
        borderRadius: '6px',
        borderLeft: '4px solid #3B82F6',
        position: 'relative'
      }}>
        💡 <strong>Подсказка:</strong> Перетаскивайте бары для изменения фактических дат. 
        Штриховка — плановые даты. <strong>Ctrl + колёсико</strong> — масштаб диаграммы. 
        Стрелки — зависимости. <span style={{ color: '#DC2626', fontWeight: '600' }}>Красные связи</span> — критический путь.
        
        {/* Ненавязчивый индикатор синхронизации */}
        {isSyncing && (
          <div style={{
            position: 'absolute',
            top: '50%',
            right: '14px',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 12px',
            background: 'rgba(59, 130, 246, 0.1)',
            borderRadius: '20px',
            fontSize: '11px',
            color: '#3B82F6',
            fontWeight: '600',
            animation: 'fadeIn 0.2s ease-in'
          }}>
            <div style={{
              width: '12px',
              height: '12px',
              border: '2px solid #3B82F6',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }} />
            Сохранение...
          </div>
        )}
      </div>
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-50%) scale(0.9); }
          to { opacity: 1; transform: translateY(-50%) scale(1); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      {renderHoveredTaskTooltip()}
    </div>
  );
};

export default ModernGanttChart;
